import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOForRoute, getPersonSchema, getFAQSchema, getSoftwareApplicationSchema, SITE_NAME } from '../utils/seo';

/**
 * SEO Component
 * Manages route-specific meta tags, Open Graph, Twitter Cards, and structured data
 */
const SEO = ({ title, description, keywords, canonical, ogImage, noindex = false }) => {
  const location = useLocation();

  // Get route-specific SEO data if custom props not provided
  const routeSEO = getSEOForRoute(location.pathname);

  // A page that passes its own title (project pages, for example) is not in the static
  // route map, so derive its canonical from the current path rather than falling back
  // to the homepage canonical and telling crawlers every page is "/".
  const origin = routeSEO.canonical.replace(/\/(work|apps|about)$/, '');
  const derivedCanonical = `${origin}${location.pathname}`.replace(/\/$/, '') || origin;

  const seo = {
    title: title || routeSEO.title,
    description: description || routeSEO.description,
    keywords: keywords || routeSEO.keywords,
    canonical: canonical || (title ? derivedCanonical : routeSEO.canonical),
    ogTitle: title || routeSEO.ogTitle,
    ogDescription: description || routeSEO.ogDescription,
    ogImage: ogImage || routeSEO.ogImage,
    twitterImage: routeSEO.twitterImage,
  };

  const personSchema = getPersonSchema();
  const faqSchema = getFAQSchema();
  const softwareSchema = getSoftwareApplicationSchema();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="author" content={SITE_NAME} />
      <meta name="keywords" content={seo.keywords} />

      {/* Canonical URL */}
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}

      {/* Robots - Allow environment-based control */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:image" content={seo.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.ogTitle} />
      <meta name="twitter:description" content={seo.ogDescription} />
      <meta name="twitter:image" content={seo.twitterImage} />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
